import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type FormEvent
} from "react";

import { css } from "@compiled/react";

const inlineInputStyle = css({ outline: 0 });

interface InlineInputPropsBase {
    value?: string;
    placeholder: string;
    animatePlaceholder?: boolean;
    onChange?: (text: string) => void;
    onEnter?: () => void;
    focus?: boolean;
    setIsInputValid?: (isValid: boolean) => void;
}
interface InlineInputPropsWithAriaLabel extends InlineInputPropsBase {
    ariaLabel: string;
    ariaLabelledBy?: never;
}
interface InlineInputPropsWithAriaLabelledBy extends InlineInputPropsBase {
    ariaLabel?: never;
    ariaLabelledBy: string;
}
export type InlineInputProps =
    | InlineInputPropsWithAriaLabel
    | InlineInputPropsWithAriaLabelledBy;

export const InlineInput = ({
    value,
    placeholder,
    animatePlaceholder = false,
    onChange,
    onEnter,
    ariaLabel,
    ariaLabelledBy,
    focus,
    setIsInputValid
}: InlineInputProps) => {
    const [isClient, setIsClient] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [doesInputContainPlaceholder, setDoesInputContainPlaceholder] =
        useState(true);
    const [initFocused, setInitFocused] = useState(false);

    const inputRef = useRef<HTMLSpanElement>(null);
    const intervalRef = useRef<number>(0);
    const previousOnChangeVal = useRef<string | undefined>(undefined);
    const previousIsInputValidVal = useRef<boolean | undefined>(undefined);
    const previousPlaceholder = useRef<string>("");
    const previousValue = useRef<string | undefined>(undefined);

    const handleOnChange = (doesInputContainPlaceholder: boolean) => {
        if (!isClient || !inputRef.current) return;

        // Call onChange if value changed (user is typing)
        const value = doesInputContainPlaceholder
            ? ""
            : inputRef.current.innerText;
        if (onChange && previousOnChangeVal.current !== value) {
            previousOnChangeVal.current = value;
            onChange(value);
        }

        // Call setIsInputValid when input changes between valid/invalid (empty = invalid)
        const isValid = !!(
            doesInputContainPlaceholder || inputRef.current.innerText
        );
        if (setIsInputValid && previousIsInputValidVal.current !== isValid) {
            previousIsInputValidVal.current = isValid;
            setIsInputValid(isValid);
        }

        // Focus on input on component load
        if (focus && !initFocused) {
            inputRef.current.focus();
            // Move cursor to the end
            const range = document.createRange();
            range.selectNodeContents(inputRef.current);
            range.collapse(false); // false = to end of node
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);

            setInitFocused(true);
        }
    };

    // Handle blur: restore placeholder if empty
    const handleOnBlur = useCallback(() => {
        if (!isClient || !inputRef.current) return;

        setIsFocused(false);

        if (inputRef.current.innerText === "") {
            // Replace empty input with the placeholder text
            inputRef.current.innerText = placeholder;
            setDoesInputContainPlaceholder(true);
            handleOnChange(true);
        } else {
            handleOnChange(doesInputContainPlaceholder);
        }
    }, [isClient, placeholder, handleOnChange]);

    // Handle input: replace placeholder on input
    const handleOnInput = useCallback(
        (e: FormEvent<HTMLSpanElement>) => {
            if (!isClient || !inputRef.current) return;

            if (doesInputContainPlaceholder) {
                const nativeEvent = e.nativeEvent as InputEvent;
                // Replace placeholder text with whatever the user typed
                inputRef.current.innerText =
                    nativeEvent.data ||
                    nativeEvent.dataTransfer?.getData("text/plain") ||
                    "";

                // Set cursor to the end
                inputRef.current.focus();
                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNodeContents(inputRef.current);
                range.collapse(false);
                selection?.removeAllRanges();
                selection?.addRange(range);

                setDoesInputContainPlaceholder(false);
            }

            handleOnChange(false);
        },
        [isClient, doesInputContainPlaceholder, handleOnChange]
    );

    // Set client flag
    useEffect(() => setIsClient(true), []);

    // Animate or set placeholder
    useEffect(() => {
        if (!isClient || !inputRef.current || value) return;

        if (previousPlaceholder.current === placeholder) return;
        previousPlaceholder.current = placeholder;

        // Set placeholder without animation
        if (!animatePlaceholder) {
            inputRef.current.innerText = placeholder;
            setDoesInputContainPlaceholder(true);
            handleOnChange(true);
            return;
        }

        // Animate placeholder
        window.clearInterval(intervalRef.current);
        inputRef.current.innerText = "";

        intervalRef.current = window.setInterval(() => {
            if (!inputRef.current) {
                window.clearInterval(intervalRef.current);
                return;
            }

            const currentNumChars = inputRef.current.innerText.length;
            const renderNumChars =
                currentNumChars +
                (placeholder[currentNumChars] === " " ? 2 : 1);
            inputRef.current.innerText = placeholder.substring(
                0,
                renderNumChars
            );

            if (renderNumChars >= placeholder.length) {
                window.clearInterval(intervalRef.current);
                setDoesInputContainPlaceholder(true);
                handleOnChange(true);
            }
        }, 35);
    }, [isClient, value, placeholder, animatePlaceholder, handleOnChange]);

    // Sync value with input text
    useEffect(() => {
        if (!isClient || !inputRef.current) return;

        if (previousValue.current === value) return;
        previousValue.current = value;

        if (typeof value !== "string") return;

        if (inputRef.current.innerText !== value) {
            if (value === "" && !isFocused) {
                inputRef.current.innerText = placeholder;
                setDoesInputContainPlaceholder(true);
                handleOnChange(true);
            } else {
                inputRef.current.innerText = value;
                setDoesInputContainPlaceholder(false);
                handleOnChange(false);
            }
        }
    }, [isClient, value, isFocused, placeholder, handleOnChange]);

    return (
        <>
            {/* Render placeholder as normal text in SSR */}
            {!isClient && <span css={inlineInputStyle}>{placeholder}</span>}

            {/*
            Render placeholder programmatically in client side,
            React does not natively support child elements in contentEditable,
            ex: `<span contentEditable="plaintext-only" ...>{placeholder}</span>`.
            It shows this warning: "Warning: A component is contentEditable and contains children managed by React.
            It is now your responsibility to guarantee that none of those nodes are unexpectedly modified or duplicated.
            This is probably not intentional."
            */}
            {isClient && (
                <span
                    contentEditable="plaintext-only"
                    role="textbox"
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    aria-placeholder={placeholder}
                    tabIndex={0}
                    onInput={handleOnInput}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            onEnter?.();
                        }
                    }}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => handleOnBlur()}
                    ref={inputRef}
                    css={inlineInputStyle}
                />
            )}
        </>
    );
};
